import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { FieldErrors, FormProvider, useForm, UseFormReturn, useWatch } from "react-hook-form";
import productCore, { createProduct, updateProduct } from "../../core/product.core";
import { getProduct } from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import { DuplicateProductRes, Product, ProductFormData, UpdateProductRes, UpdateProductRes422 } from "../../types/product";
import { LocalInputDefaultLocale, LocaleTextAresDefaultLocale } from "../locale/locale.comp";
import CategoryInput from "../reuseables/categoryInput.comp";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { EditingMode, LabelAndInput } from "../reuseables/form.comp";
import AttachedProduct from "./attachedProducts.comp";
import { Availability } from "./availabilityEditor";
import ChildProductQuickEditor from "./childProductQuickEditor.comp";
import CustomProductItems from "./customProductItems.comp";
import ProductAttributeEditor from "./productAttributeEditor";
import ProductBarCodeEditor from "./productBarcodeEditor.comp";
import ProductBrandEditor from "./productBrandSelector.comp";
import ProductShelfSelector from "./productShelfSelector.comp";
import ProductExcludePlacementEditor from "../common/excludePlacementEditor.comp";
import ProductMediaEditor from "./productMedia.comp";
import ProductPrices from "./productPrices.comp";
import ProductStatusEditor from "./productStatusEditor";
import ProductTypeSelector from "./productTypeSelector.comp";
import QRCodeDisplay from "./qrCodeDispay.comp";
import RelatedProductsInput from "./relatedProductsInput.comp";
import LoadingOvalIcon from "../../icons/loadingOval.icon";
import CheckIcon from "../../icons/check.icon";
import ExclamationTriangleIcon from "../../icons/exclamationTriangle.icon";
import RecycleProductSelector from "./recycleProductSelector.comp";
import ChildProductList from "./childProductList.comp";
import TagMultipleSelectorComp from "../tag/tagMutipleSelector.comp";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import tagCore from "../../core/tag.core";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import localeCore from "../../core/locale.core";
import CustomSuggestionItems from "./customSuggestionItems.comp";
import { convertProductToForm } from "../../core/product.core";
import BoxConversionEditor from "./boxConversionEditor";
import ExcludeShipOptionsEditor from "./excludeShipOptionsEditor.comp";
import BundleProductEditor from "./bundleProductEditor.comp";
import ProductDescriptionEditor from "./productDescriptionEditor.comp";

const cssLabel = "text-xs font-semibold";

export interface PropsCreate {
  editingMode: EditingMode.create;
}

export interface PropsEdit {
  editingMode: EditingMode.edit;
  productId: string;
}

type SubmitBtnState = "init" | "updating" | "success" | "error";

export default function ProductEditor(props: PropsCreate | PropsEdit) {
  const editingMode = props.editingMode;

  const methods = useForm<ProductFormData>({
    defaultValues: {
      name: {},
      sku: "",
      status: "active",
      membershipPrices: [],
      retailPrices: [],
      preOrderRemarks: {},
      excludeShipOptionIds: [],
      isRewardRedeemable: true,
    },
  });
  const router = useRouter();
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const { data: editingProduct } = useQuery({
    queryKey: ["productSingle", (props as PropsEdit).productId],
    queryFn: (context) => getProduct(context.queryKey[1]),
    enabled: editingMode === EditingMode.edit && props.productId !== undefined,
    staleTime: 1, // always stale
  });

  const [submitState, setSubmitState] = useState<SubmitBtnState>("init");
  const [error422, setError422] = useState<FieldErrors<ProductFormData>>();

  useEffect(() => {
    if (editingProduct) methods.reset(convertProductToForm(editingProduct));
  }, [editingProduct, methods]);

  function handleUpdateError422(resData: UpdateProductRes422) {
    setSubmitState("error");

    const errorObj: FieldErrors<ProductFormData> = {};

    for (const [key, value] of Object.entries(resData.errors)) {
      errorObj[key] = { type: "validate" };
    }
    // convert to FieldErrors
    setError422(errorObj);
  }

  const onSubmit = async (data: ProductFormData) => {
    let resData: UpdateProductRes | UpdateProductRes422 | DuplicateProductRes | undefined;
    switch (editingMode) {
      case EditingMode.create:
        const createProductRes = await createProduct(data);

        resData = createProductRes.data;
        if (createProductRes.status !== 200) {
          setSubmitState("error");
          return;
        }
        // console.log(data);
        break;
      case EditingMode.edit:
        setSubmitState("updating");
        const res = await updateProduct(props.productId, data);

        if (res.status === 422) {
          handleUpdateError422(res.data as UpdateProductRes422);
          return;
        }

        if (res.status !== 200) {
          setSubmitState("error");
          console.error("res.error", (res.data as any)?.errors);
          return;
        }

        resData = res.data;
        setSubmitState("success");
        break;
      default:
        break;
    }

    if (resData && (resData as DuplicateProductRes).errors?.length > 0) {
      productCore.setDuplicateError(methods, resData as DuplicateProductRes);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["products"] });

    if (props.editingMode === EditingMode.edit) {
      await queryClient.invalidateQueries({ queryKey: ["productSingle", props.productId] });
    }

    router.push("/products");
  };

  if (props.editingMode === EditingMode.edit) {
    if (!editingProduct) return null;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
        <EditorSection>
          <Title>{t(msg.product.name)}</Title>
          <LocalInputDefaultLocale register={methods.register} fieldName="name" />
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.product.description)}</Title>
          {/* <LocaleTextAresDefaultLocale register={methods.register} fieldName="description" /> */}
          <ProductDescriptionEditor
            productId={props.editingMode === EditingMode.edit ? props.productId : undefined}
            register={methods.register}
            fieldName="description"
          />
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.product.shortDescription)}</Title>
          <LocaleTextAresDefaultLocale register={methods.register} fieldName="shortDescription" />
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.category.category)}</Title>
          <CategoryInput register={() => methods.register("categoryIds")} />
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.product.generalInfo)}</Title>
          <div className="text-xs flex flex-col gap-4">
            <label>
              <span className={`${cssLabel} mr-2`}>{t(msg.product.isPromotionProduct)}</span>
              <input type="checkbox" {...methods.register("isPromotionProduct")} />
            </label>
          </div>
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.product.sku)}</Title>
          <div className="space-y-4">
            <div>
              <LabelAndInput
                register={methods.register}
                fieldName="sku"
                label={t(msg.product.sku)}
                errors={methods.formState.errors}
                registerOptions={{ required: t(msg.product.skuR) }}
              />
            </div>
            {editingProduct && (
              <div>
                <label>{t(msg.scanner.qrCode)}</label>
                <QRCodeDisplay product={editingProduct} />
              </div>
            )}

            <div>
              <label>{t(msg.scanner.barcode)}</label>
              <ProductBarCodeEditor methods={methods} />
            </div>
          </div>
        </EditorSection>
        <EditorSection>
          <Title>{t(msg.shop.cost)}</Title>
          <LabelAndInput
            label={t(msg.shop.cost)}
            register={methods.register}
            fieldName="cost"
            topLabelCss="block"
            errors={error422}
            type="number"
          />
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.price2)}</Title>
          <ProductPrices methods={methods} editingMode={editingMode} />
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.brand)}</Title>
          <ProductBrandEditor methods={methods as any} />
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.shelf)}</Title>
          <ProductShelfSelector methods={methods as any} />
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.tag)}</Title>
          <div className="space-y-4">
            <TagMultipleSelectorComp methods={methods} name="tagId" />
            <SelectedTags methods={methods} />
          </div>
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.productType)}</Title>
          <div className="space-y-8">
            <ProductTypeSelector methods={methods} />

            <CustomProductItems
              parentProduct={editingProduct}
              editingMode={editingMode}
              productType={methods.watch("productType")}
            />
            <CustomSuggestionItems
              parentProduct={editingProduct}
              editingMode={editingMode}
              productType={methods.watch("productType")}
            />
          </div>
        </EditorSection>
        {methods.watch("productType") === "bundle" && (
          <EditorSection>
            <Title>{t(msg.product.bundleProducts)}</Title>
            <BundleProductEditor methods={methods} />
          </EditorSection>
        )}
        <Variation editingProduct={editingProduct} methods={methods} />

        <EditorSection>
          <Title>{t(msg.attribute.attributes)}</Title>
          <ProductAttributeEditor methods={methods as any} />
        </EditorSection>

        {editingProduct && editingProduct.productType !== "custom" ? (
          <EditorSection>
            <Title>{t(msg.product.attachedProducts)}</Title>
            <AttachedProduct methods={methods} parentProduct={editingProduct} />
          </EditorSection>
        ) : null}

        <EditorSection>
          <Title>{t(msg.product.productVisibility)}</Title>
          <div>
            <ProductStatusEditor methods={methods} />
            <ProductExcludePlacementEditor methods={methods} />
            <Availability methods={methods} />
            <div className="mt-4">
              <LabelAndInput
                register={methods.register}
                fieldName="expiaryDate"
                label={t(msg.product.expiaryDate) || "Expiary Date"}
              />
            </div>
          </div>
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.preOrder)}</Title>
          <div className="flex flex-col gap-4">
            <label>
              <span className={`${cssLabel} mr-2`}>{t(msg.product.isPreOrder)}</span>
              <input type="checkbox" {...methods.register("isPreOrder")} />
            </label>

            <LabelAndInput
              step=".01"
              type="number"
              register={methods.register}
              fieldName="preOrderLimit"
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.product.preOrderLimit)}
              errors={methods.formState.errors}
            />

            <LabelAndInput
              type="date"
              register={methods.register}
              fieldName="preOrderEstArrival"
              label={t(msg.product.estArrivalDate)}
            />

            <div className="mt-2">
              <label className="block mb-1">{t(msg.product.preOrderRemarks)}</label>
              <LocaleTextAresDefaultLocale register={methods.register} fieldName="preOrderRemarks" />
            </div>
          </div>
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.boxConversion)}</Title>
          <BoxConversionEditor methods={methods} />
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.reward.reward)}</Title>
          <div className="flex flex-col gap-4">
            <LabelAndInput
              label={t(msg.shop.rewardPayout)}
              register={methods.register}
              fieldName="rewardPayout"
              topLabelCss="block"
              errors={error422}
            />
            <div className="text-xs flex flex-col gap-4">
              <label>
                <span className={`${cssLabel} mr-2`}>{t(msg.reward.isRewardRedeemable)}</span>
                <input type="checkbox" {...methods.register("isRewardRedeemable")} />
              </label>
            </div>
          </div>
        </EditorSection>

        <EditorSection>
          <Title>{t(msg.product.misc)}</Title>
          <div className="flex flex-col gap-4">
            <RelatedProductsInput methods={methods} />
            <RecycleProductSelector methods={methods} />
          </div>
        </EditorSection>

        {editingMode === EditingMode.edit ? (
          <EditorSection>
            <Title>{t(msg.ship.shipping)}</Title>
            <div className="flex flex-col gap-4">
              <ExcludeShipOptionsEditor editingProduct={editingProduct} methods={methods} />
            </div>
          </EditorSection>
        ) : null}

        <EditorSection>
          <Title>{t(msg.media.media)}</Title>
          <ProductMediaEditor methods={methods} />
        </EditorSection>
        <SubmitButton editingMode={editingMode} state={submitState} />
      </form>
    </FormProvider>
  );
}

function SubmitButton({ editingMode, state }: { editingMode: EditingMode; state: SubmitBtnState }) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();

  function getSubmitBtnText() {
    switch (editingMode) {
      case EditingMode.create:
        return t(msg.product.add);
      case EditingMode.edit:
        return t(msg.product.edit);
    }
  }

  return (
    <SubmitPanel>
      <button type="button" onClick={() => push("/products")}>
        {t(msg.form.cancel)}
      </button>
      <div className="flex gap-2 items-center">
        <input type="submit" value={getSubmitBtnText()} />
        {state === "updating" && <LoadingOvalIcon />}
        {state === "success" && <CheckIcon className="w-6 h-6 text-green-500" />}
        {state === "error" && <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />}
      </div>
    </SubmitPanel>
  );
}

function Variation({ editingProduct, methods }: { editingProduct?: Product; methods: UseFormReturn<ProductFormData, any> }) {
  const { t, msg } = useTranslation();
  const productType = useWatch({ name: "productType", control: methods.control });

  if (!editingProduct) return null;
  if (productType !== "variationParent") return null;

  return (
    <>
      <EditorSection>
        <Title>{t(msg.product.variationChild)}</Title>
        <ChildProductList parentProduct={editingProduct} />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.product.addChild)}</Title>
        <ChildProductQuickEditor parentProduct={editingProduct} />
      </EditorSection>
    </>
  );
}

function SelectedTags({ methods }: { methods: UseFormReturn<ProductFormData> }) {
  const tagIds = useWatch({ name: "tagId", control: methods.control }) as string[];
  const { locale } = useRouter();
  const {
    data: tags,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["tags"],
    queryFn: tagCore.listTags,
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!tags) return null;

  if (!tagIds) return null;
  return (
    <div>
      {tagIds.map((tagId) => {
        const foundTag = tags.find((c) => c.id === tagId);
        if (!foundTag) return undefined;
        return <p key={tagId}>{localeCore.getLocaleValueFallback(foundTag.name, locale!)}</p>;
      })}
    </div>
  );
}
